import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";
import { api } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CardSkeleton } from "../components/Skeletons";

const COLORS = ["#534AB7", "#1D9E75", "#F59E0B", "#EF4444", "#8B5CF6"];

const tooltipStyle = {
  contentStyle: {
    background: "rgba(15, 14, 26, 0.96)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 10,
    color: "#F0EFF8",
    fontSize: 12,
    padding: "8px 12px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
  },
  itemStyle: { color: "#F0EFF8" },
  labelStyle: { color: "#AFA9EC", fontWeight: 600, marginBottom: 4 },
};

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="k-glass rounded-2xl p-6"
      data-testid={testid}
    >
      <h3 className="font-semibold">{title}</h3>
      <p className="text-xs text-muted-foreground mb-4">{subtitle}</p>
      <div className="h-72">{children}</div>
    </motion.div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState("90");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await api.get("/analytics", { params: { days } });
      setData(data);
      setLoading(false);
    })();
  }, [days]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="analytics-title">
            Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Trends, performance, and decisions.</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-36 k-glass" data-testid="analytics-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 6 months</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading || !data ? (
        <div className="grid lg:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <ChartCard title="Monthly PO value" subtitle="Total PO value per month" testid="chart-monthly">
            <ResponsiveContainer>
              <LineChart data={data.monthly_trend}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip {...tooltipStyle} formatter={(v) => `₹${Number(v).toLocaleString()}`} />
                <Line type="monotone" dataKey="value" stroke="#534AB7" strokeWidth={3} dot={{ fill: "#534AB7", r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Approval rate by department" subtitle="% approved over total requests" testid="chart-approval">
            <ResponsiveContainer>
              <BarChart data={data.approval_rate}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="department" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit="%" />
                <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} />
                <Bar dataKey="rate" radius={[8, 8, 0, 0]}>
                  {data.approval_rate.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Top vendors by volume" subtitle="Total PO value awarded" testid="chart-vendors">
            <ResponsiveContainer>
              <BarChart data={data.top_vendors} layout="vertical">
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="vendor" type="category" tick={{ fontSize: 11 }} width={120} />
                <Tooltip {...tooltipStyle} formatter={(v) => `₹${Number(v).toLocaleString()}`} />
                <Bar dataKey="value" fill="#1D9E75" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Lead time distribution" subtitle="Days between dispatch and delivery" testid="chart-lead">
            <ResponsiveContainer>
              <BarChart data={data.lead_histogram}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="count" fill="#534AB7" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}
    </div>
  );
}
